import Cookies from 'js-cookie';
import type { RouteLocationRaw, Router } from 'vue-router';
import AuthService from '../runtime/services/AuthService';
import { getAuthRedirect } from '../runtime/utils/authRedirect';

export class AuthHelper implements Auth
{
    private router: Router;
    private i18n: I18n;
    private store: AuthStore;
    private options: AuthOptions;
    private tokenTimeoutHandler: any;
    private sessionDuration: number;
    private online: boolean = true;

    public constructor(router: Router, i18n: I18n, store: AuthStore, options: AuthOptions)
    {
        this.router = router;
        this.i18n = i18n;
        this.store = store;
        this.options = options;

        this.initRouter();
        this.initImpersonation();
    }

    private initRouter()
    {
        this.router.beforeEach(async (to: RouteLocation, from: RouteLocation, next: any) =>
        {
            if (this.store.loaded === false && to.name != from?.name)
            {
                // Sprawdzenie ważności access tokena po odświeżeniu przeglądarki
                if (!this.check())
                {
                    await this.validate();
                }

                // Uzyskanie nowego access tokena za pomocą refresh tokena (opcja "remember me")
                if (!this.check())
                {
                    await this.recover();
                }

                // Logowanie Windows
                if (!this.check() && this.options.windows)
                {
                    await this.ntlm();
                }

                // Pobranie danych użytkownika
                if (this.check())
                {
                    await this.fetch();
                }
            }

            this.store.setLoaded(true);

            next();
        });
    }

    private async initSession()
    {
        await this.setSessionDuration();

        const updateLastActivity = () =>
        {
            this.lastActivity = Date.now();
        };
        const handleNetworkOnline = (event: any) =>
        {
            this.online = true;
        };
        const handleNetworkOffline = (event: any) =>
        {
            this.online = false;
        };

        window.addEventListener('online', handleNetworkOnline);
        window.addEventListener('offline', handleNetworkOffline);
        document.body.addEventListener('click', updateLastActivity);
        document.body.addEventListener('mousemove', updateLastActivity);
        document.body.addEventListener('keypress', updateLastActivity);

        const intervalId = setInterval(async () =>
        {
            this.lastActivity = this.lastActivity || Date.now(); // ms

            // Z wyłączoną opcją "remember me" sesja wygasa po przekroczeniu okresu nieaktywności
            if (this.check() && this.store.rememberMe === false)
            {
                const sessionTime = this.sessionDuration * 60 * 1000; // ms
                const timeLeft = this.lastActivity + sessionTime - Date.now(); // ms

                if (timeLeft < 0)
                {
                    this.logout();
                }
            }
        },
        1000);

        const handleStorageChange = (e: StorageEvent) =>
        {
            if (e.key === 'last-activity' && e.oldValue != null && e.newValue == null)
            {
                this.clearAll();
                this.router.push(this.options.routes.loginPage).catch(() => {});
            }
        };

        window.addEventListener('storage', handleStorageChange);

        this.destroySession = () =>
        {
            document.body.removeEventListener('keypress', updateLastActivity);
            document.body.removeEventListener('mousemove', updateLastActivity);
            document.body.removeEventListener('click', updateLastActivity);
            clearInterval(intervalId);
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('offline', handleNetworkOnline);
            window.removeEventListener('online', handleNetworkOnline);
        };
    }

    private destroySession() {}

    public resetSession()
    {
        this.destroySession();

        return this.initSession();
    }

    private initImpersonation()
    {
        const impersonated = localStorage.getItem('impersonated');

        if (impersonated)
        {
            this.store.impersonate(Number(impersonated));
        }

        window.addEventListener('storage', (e) =>
        {
            if (e.key === 'impersonated')
            {
                if (e.newValue != null)
                    this.store.impersonate(Number(e.newValue));
                else
                    this.store.unimpersonate();
            }
        });
    }

    private get lastActivity()
    {
        return Number(localStorage.getItem('last-activity') || 0);
    }

    private set lastActivity(value: number)
    {
        localStorage.setItem('last-activity', value.toString());
    }

    private async setSessionDuration()
    {
        const userSessionDuration = await AuthService.getSessionSettings();

        this.sessionDuration = userSessionDuration.sessionDuration > 0
            ? userSessionDuration.sessionDuration
            : 60; // minuty
    }

    private clearSession()
    {
        localStorage.removeItem('last-activity');
    }

    private decryptToken(token: string)
    {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(window.atob(base64));

        return payload;
    }

    private setToken(tokens: TokenModel)
    {
        if (tokens?.token.length > 0)
        {
            this.store.setToken(tokens.token);
            this.store.setAuthenticated(true);

            const clockSkew = 30 * 1000; // 30 sekund przed wygaśnięciem tokena musimy go odświeżyć
            const payload = this.decryptToken(tokens.token);
            const timeLeft = Number(payload.exp) * 1000 - (new Date()).getTime() - clockSkew;

            if (timeLeft > 0)
            {
                this.tokenTimeoutHandler = setTimeout(async () =>
                {
                    try
                    {
                        this.refresh(true);
                    }
                    catch
                    {
                        this.logout();
                    }
                },
                timeLeft);
            }
        }
    }

    private clearToken()
    {
        clearTimeout(this.tokenTimeoutHandler);
        this.setToken(null);
        this.store.setAuthenticated(false);
    }

    private clearAll()
    {
        if (this.online)
        {
            this.store.setRememberMe(false);
            this.store.clearIdentity();
            this.unimpersonate();
            this.clearSession();
            this.clearToken();
            this.destroySession();
        }
    }

    private checkPermissions(token: Record<string, string>)
    {
        if (this.options.defaultPolicy(token) == false)
        {
            throw (() =>
            {
                return {
                    code: 400,
                    message: '[[[Nie masz uprawnień do panelu administracyjnego.]]]',
                    data: null as any,
                    inner: null as any
                };
            })();
        }
    }

    public async verifyTwoFactor(
        challengePublicId: string,
        code: string,
        rememberMe: boolean,
        forceLogin: boolean,
        redirect?: string,
        redirectOverride?: RouteLocationRaw
    )
    {
        try
        {
            const device = await AuthService.getDeviceInfo();
            const result = await AuthService.verifyTwoFactor({
                challengePublicId,
                code,
                deviceId: device.deviceId,
                deviceName: device.deviceName,
                rememberMe,
                forceLogin
            });
            const token = this.decryptToken(result.token);

            this.checkPermissions(token);
            this.setToken(result);

            await this.fetch();

            this.initSession();
            this.store.setRememberMe(rememberMe);

            const destination = redirectOverride ?? getAuthRedirect(redirect) ?? this.options.routes.homePage;

            this.router.push(destination).catch(() =>
            {
                window.location.replace('/');
            });
        }
        catch (ex)
        {
            this.clearAll();

            throw ex;
        }
    }

    public async login(username: string, password: string, captchaResult: string = null, rememberMe: boolean = false, forceLogin: boolean = false, redirect?: string)
    {
        try
        {
            this.clearAll();

            const result = await AuthService.login(username, password, captchaResult, rememberMe, forceLogin);
            const token = this.decryptToken(result.token);

            this.checkPermissions(token);
            this.setToken(result);

            await this.fetch();

            this.initSession();
            this.store.setRememberMe(rememberMe);

            const destination = getAuthRedirect(redirect) ?? this.options.routes.homePage;

            this.router.push(destination).catch(() => {});
        }
        catch (ex)
        {
            this.clearAll();

            throw ex;
        }
    }

    public async logout(redirect?: RouteLocationRaw)
    {
        if (this.store.authenticated)
        {
            try
            {
                this.store.setLoaded(false);
                this.clearToken();
                await AuthService.logout();
            }
            catch
            {
                // ignore errors
            }
            finally
            {
                this.clearAll();
                this.router.push(redirect || this.options.routes.loginPage).catch(() => {});
                this.store.setLoaded(true);
            }
        }
    }

    public async validate(throwError: boolean = false)
    {
        try
        {
            const result = await AuthService.validateToken();
            const token = this.decryptToken(result.token);

            this.checkPermissions(token);
            this.setToken(result);

            this.initSession();
            this.store.setRememberMe(result.refresh !== null);

            return token;
        }
        catch (ex)
        {
            this.clearAll();

            if (throwError)
                throw ex;
        }

        return null;
    }

    public async refresh(throwError: boolean = false)
    {
        try
        {
            const result = await AuthService.refreshToken();
            const token = this.decryptToken(result.token);

            this.checkPermissions(token);
            this.setToken(result);

            return token;
        }
        catch (ex)
        {
            this.clearAll();

            if (throwError)
                throw ex;
        }

        return null;
    }

    public async recover(throwError: boolean = false)
    {
        try
        {
            const result = await AuthService.recoverToken();
            const token = this.decryptToken(result.token);

            this.checkPermissions(token);
            this.setToken(result);

            this.initSession();
            this.store.setRememberMe(true);

            return token;
        }
        catch (ex)
        {
            this.clearAll();

            if (throwError)
                throw ex;
        }

        return null;
    }

    public async ntlm(throwError: boolean = false)
    {
        try
        {
            const result = await AuthService.ntlm();
            const token = this.decryptToken(result.token);

            this.checkPermissions(token);
            this.setToken(result);
            this.initSession();

            return token;
        }
        catch (ex)
        {
            this.clearAll();

            if (throwError)
                throw ex;
        }

        return null;
    }

    public token()
    {
        return this.store.token;
    }

    public claims()
    {
        return this.decryptToken(this.token());
    }

    public async fetch()
    {
        try
        {
            const result = await AuthService.getIdentity(true);

            this.store.setIdentity(result);

            if (result.preferredLanguage)
                this.i18n.setLocale(result.preferredLanguage);
        }
        catch
        {
            this.clearAll();
        }
    }

    public user()
    {
        return this.store.identity;
    }

    public userId()
    {
        return this.impersonating() || this.user().id;
    }

    public check()
    {
        return this.store.authenticated;
    }

    public ready()
    {
        return this.store.loaded;
    }

    public reload()
    {
        this.store.setLoaded(false);
        setTimeout(() => this.store.setLoaded(true), 100);
    }

    public impersonating()
    {
        return this.store.impersonated as number;
    }

    public impersonatingValue()
    {
        if (this.token())
        {
            const payload = this.decryptToken(this.token());
            const value = window.btoa(`${this.impersonating().toString()}#${payload.jti}`);

            if (Cookies.get('access-impersonate') != value)
            {
                Cookies.set('access-impersonate', value, { path: '/', secure: true, sameSite: 'None' });
            }

            return value;
        }

        return null;
    }

    public impersonate(id: number)
    {
        this.store.impersonate(id);
        localStorage.setItem('impersonated', id.toString());
    }

    public unimpersonate()
    {
        this.store.unimpersonate();
        localStorage.removeItem('impersonated');
        Cookies.remove('access-impersonate', { path: '/', secure: true, sameSite: 'None' });
    }
}

export default defineNuxtPlugin({
    name: 'auth',
    dependsOn: ['nuxt:router', 'axios', 'i18n'],
    async setup(nuxtApp)
    {
        const { $router, $config, $i18n } = useNuxtApp();
        const store = useAuthStore();
        const options = {
            ...useAuthOptions(),
            windows: $config.public.authWindows,
            azure: $config.public.authAzure
        };
        const helper = new AuthHelper($router as any, $i18n, store, options) as Auth;

        return {
            provide: {
                auth: helper
            }
        };
    }
});

declare module '@vue/runtime-core'
{
    interface ComponentCustomProperties
    {
        $auth: Auth;
    }
}
